import { Copy, Check, ExternalLink, Clock } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const DAY = 86400000;
const romeDay = (d) => new Date(d.toLocaleString("en-US", { timeZone: "Europe/Rome" })).setHours(0, 0, 0, 0);

export const expiryLabel = (end) => {
  if (!end) return null;
  const d = new Date(end);
  if (isNaN(d) || d <= new Date()) return null;
  const days = Math.round((romeDay(d) - romeDay(new Date())) / DAY);
  if (days <= 0) return { text: "Scade oggi", urgent: true };
  if (days === 1) return { text: "Scade domani", urgent: true };
  return { text: `Scade tra ${days} giorni`, urgent: days <= 3 };
};

export const ExpiryBadge = ({ end, id, className = "" }) => {
  const exp = expiryLabel(end);
  if (!exp) return null;
  return (
    <span data-testid={id} className={`inline-flex items-center gap-1 font-mono text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${exp.urgent ? "bg-red-500/15 text-red-500" : "bg-surfaceTertiary text-onSurfaceSecondary"} ${className}`}>
      <Clock size={11} /> {exp.text}
    </span>
  );
};

const fmtDate = (v) => (v ? new Date(v).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric", timeZone: "Europe/Rome" }) : null);

export const CouponDetails = ({ coupon: c, onClose, onCopy, copied }) => (
  <Dialog open={!!c} onOpenChange={(o) => !o && onClose()}>
    {c && (
      <DialogContent
        data-testid="coupon-details"
        className="flex flex-col bg-surfaceSecondary border-hair text-onSurface max-h-[85vh] top-auto bottom-0 translate-y-0 rounded-t-2xl sm:top-[50%] sm:bottom-auto sm:translate-y-[-50%] sm:rounded-2xl"
      >
        <div className="flex items-center gap-2 pr-6">
          <span className="font-mono text-[11px] font-bold uppercase tracking-wider text-brand truncate">{c.advertiser_name}</span>
          <ExpiryBadge end={c.end_date} id="coupon-details-expiry" />
        </div>
        <DialogTitle className="text-lg font-semibold leading-snug">{c.title}</DialogTitle>
        <DialogDescription asChild>
          <div className="space-y-4 text-sm text-onSurfaceSecondary overflow-y-auto min-h-0 flex-1 pr-1 -mr-1">
            {c.description && <p data-testid="coupon-details-description" className="whitespace-pre-line">{c.description}</p>}
            {c.terms && (
              <div>
                <h4 className="font-mono text-[11px] font-bold uppercase tracking-wider text-subtle mb-1">Condizioni d'uso</h4>
                <p data-testid="coupon-details-terms" className="whitespace-pre-line text-xs">{c.terms}</p>
              </div>
            )}
            {c.end_date && <p className="font-mono text-xs text-subtle">Valido fino al {fmtDate(c.end_date)}</p>}
          </div>
        </DialogDescription>
        <div className="space-y-2 shrink-0 pt-1">
          {c.voucher_code && (
            <button
              type="button"
              onClick={() => onCopy(c.voucher_code, c.promotion_id)}
              data-testid="coupon-details-copy"
              className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg border border-dashed border-brand/50 bg-brandTertiary/40 text-brand font-mono text-sm font-bold hover:bg-brandTertiary transition-colors"
            >
              <span className="truncate">{c.voucher_code}</span>
              {copied === c.promotion_id ? <Check size={15} /> : <Copy size={15} />}
            </button>
          )}
          {c.tracking_url && (
            <a
              href={c.tracking_url}
              target="_blank"
              rel="noopener noreferrer sponsored"
              data-testid="coupon-details-link"
              className="w-full flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg bg-brand text-onBrand text-sm font-semibold hover:bg-brandSecondary transition-colors"
            >
              Vai all'offerta <ExternalLink size={14} />
            </a>
          )}
        </div>
      </DialogContent>
    )}
  </Dialog>
);
